import { createContext, useContext } from 'react';
import type { ProfileWithRolesShape } from '@eduborg/shared';

/**
 * Auth state lives in a React context. The token is stored in localStorage
 * (resilient across tabs and reloads) AND set as an httpOnly cookie by the
 * server (resilient against XSS-driven token exfiltration). Two-channel.
 *
 * The context only exposes derived state + actions; the raw token is opaque
 * to consumers. Components read `profile`, `isAuthenticated`, `roles`.
 */

export type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; profile: ProfileWithRolesShape; token: string };

export type AuthContextValue = {
  state: AuthState;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-fetch /me, e.g. after a profile update that changed roles. */
  refresh: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}

export const TOKEN_STORAGE_KEY = 'eduborg.token';
